import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { nicknameOnly } from '../../utils/clientIdentity';

export default function ClientList() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    if (q) {
      // normalise: treat spaces and hyphens as interchangeable so "tls 30" matches "TLS-30"
      const qAlt = q.includes('-') ? q.replace(/-/g, ' ') : q.replace(/ /g, '-');

      const buildFilter = (term) =>
        `full_name.ilike.%${term}%,email.ilike.%${term}%,passport_number.ilike.%${term}%,client_code.ilike.%${term}%,phone.ilike.%${term}%,mobile.ilike.%${term}%`;

      let orFilter = buildFilter(q);
      if (qAlt !== q) orFilter += ',' + buildFilter(qAlt);

      // also search by agent tls_code (both forms)
      const terms = [...new Set([q, qAlt])];
      const agentFilters = terms.map(t => `tls_code.ilike.%${t}%`).join(',');
      const { data: matchedAgents } = await supabase.from('agents').select('id').or(agentFilters);
      if (matchedAgents?.length) {
        const ids = matchedAgents.map(a => a.id).join(',');
        orFilter += `,care_of_agent_id.in.(${ids})`;
      }

      const { data } = await supabase.from('clients').select('*, agents:care_of_agent_id(tls_code, full_name)').order('full_name').or(orFilter);
      setClients(data || []);
    } else {
      const { data } = await supabase.from('clients').select('*, agents:care_of_agent_id(tls_code, full_name)').order('full_name');
      setClients(data || []);
    }

    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Clients</div><div className="page-subtitle">All clients</div></div>
        <Link to="/clients/new" className="btn btn-primary">+ New Client</Link>
      </div>
      <div className="card">
        <div style={{ marginBottom: 16 }}>
          <input className="form-input" placeholder="Search by name, email, passport, code..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 400 }} />
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Email</th><th>Phone</th><th>Passport</th><th>Nationality</th><th></th></tr></thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600, color: '#f07040' }}>{c.agents?.tls_code || '—'}</td>
                    <td>
                      <Link to={`/clients/${c.id}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>
                        {nicknameOnly(c.client_code) || c.client_code}
                      </Link>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.email || ''}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.phone || ''}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{c.passport_number || ''}</td>
                    <td>{c.nationality || ''}</td>
                    <td><Link to={`/clients/${c.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                  </tr>
                ))}
                {clients.length === 0 && <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-title">No clients found</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
